import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaMapMarkerAlt, FaClock, FaDollarSign, FaCalendarAlt, FaSpinner, FaUsers,
  FaSeedling, FaHardHat, FaIndustry, FaHome, FaBolt, FaTruck,
  FaShoppingCart, FaUmbrellaBeach, FaUniversity, FaHospital,
  FaGraduationCap, FaLaptop, FaFilm, FaBriefcase, FaLandmark,
  FaPalette, FaFutbol, FaLeaf, FaPlane, FaHandshake
} from 'react-icons/fa';
import { listingService } from '../services/listingService';
import { useAuth } from '../contexts/AuthContext';
import { locationData, provinces } from '../utils/locationData';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FiltersSection = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterLabel = styled.label`
  font-weight: 500;
  margin-bottom: 0.3rem;
  color: ${props => props.theme.colors.text};
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.4rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.35rem;
  }
`;

const GigsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const GigCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 20px ${props => props.theme.colors.shadowHover};
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const GigIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.$gradient || 'linear-gradient(135deg, #FF6B6B, #FF8E53)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const GigTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const GigDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const GigDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const DateBadge = styled.span`
  background: #e3f2fd;
  color: #1565c0;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }
`;

const ResetButton = styled.button`
  background-color: #FF6B6B;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #E85A5A;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.75rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #FF6B6B;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Industry icon mapping
const getIndustryIcon = (industry) => {
  const iconMap = {
    'Agriculture & Forestry': { icon: FaSeedling, gradient: 'linear-gradient(135deg, #4CAF50, #81C784)' },
    'Mining & Natural Resources': { icon: FaHardHat, gradient: 'linear-gradient(135deg, #795548, #A1887F)' },
    'Manufacturing': { icon: FaIndustry, gradient: 'linear-gradient(135deg, #607D8B, #90A4AE)' },
    'Construction & Real Estate': { icon: FaHome, gradient: 'linear-gradient(135deg, #FF9800, #FFB74D)' },
    'Utilities & Energy': { icon: FaBolt, gradient: 'linear-gradient(135deg, #FFC107, #FFD54F)' },
    'Transportation & Logistics': { icon: FaTruck, gradient: 'linear-gradient(135deg, #3F51B5, #7986CB)' },
    'Retail & Wholesale Trade': { icon: FaShoppingCart, gradient: 'linear-gradient(135deg, #E91E63, #F06292)' },
    'Hospitality & Tourism': { icon: FaUmbrellaBeach, gradient: 'linear-gradient(135deg, #00BCD4, #4DD0E1)' },
    'Financial Services': { icon: FaUniversity, gradient: 'linear-gradient(135deg, #4CAF50, #66BB6A)' },
    'Healthcare & Life Sciences': { icon: FaHospital, gradient: 'linear-gradient(135deg, #F44336, #EF5350)' },
    'Education & Training': { icon: FaGraduationCap, gradient: 'linear-gradient(135deg, #9C27B0, #BA68C8)' },
    'Information & Communication Technology (ICT)': { icon: FaLaptop, gradient: 'linear-gradient(135deg, #2196F3, #64B5F6)' },
    'Technology & IT': { icon: FaLaptop, gradient: 'linear-gradient(135deg, #2196F3, #64B5F6)' },
    'Media & Entertainment': { icon: FaFilm, gradient: 'linear-gradient(135deg, #673AB7, #9575CD)' },
    'Professional & Business Services': { icon: FaBriefcase, gradient: 'linear-gradient(135deg, #455A64, #78909C)' },
    'Public Sector & Non-Profit': { icon: FaLandmark, gradient: 'linear-gradient(135deg, #5D4037, #8D6E63)' },
    'Arts, Culture & Creative Industries': { icon: FaPalette, gradient: 'linear-gradient(135deg, #FF5722, #FF8A65)' },
    'Sports & Recreation': { icon: FaFutbol, gradient: 'linear-gradient(135deg, #8BC34A, #AED581)' },
    'Environmental & Sustainability': { icon: FaLeaf, gradient: 'linear-gradient(135deg, #009688, #4DB6AC)' },
    'Aerospace & Defense': { icon: FaPlane, gradient: 'linear-gradient(135deg, #3F51B5, #5C6BC0)' },
    'Consumer Services': { icon: FaHandshake, gradient: 'linear-gradient(135deg, #FF9800, #FFAB40)' }
  };
  
  return iconMap[industry] || { icon: FaUsers, gradient: 'linear-gradient(135deg, #FF6B6B, #FF8E53)' };
};

// Helper function to capitalize values from database
const capitalizeValue = (value) => {
  if (!value || value === 'Not specified') return value;
  // Handle hyphenated values (e.g., 'on-site' -> 'On-site')
  return value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
};

const skills = [
  "All Skills",
  "Agriculture & Farming",
  "Communication & Public Relations",
  "Construction & Trades",
  "Customer Service & Support",
  "Data & Analytics",
  "Design & Creative Arts",
  "Education & Training",
  "Engineering & Technical",
  "Environmental & Sustainability",
  "Finance & Accounting",
  "Healthcare & Medical",
  "Hospitality & Tourism",
  "Human Resources & People Management",
  "Legal & Compliance",
  "Logistics & Supply Chain",
  "Manufacturing & Production",
  "Marketing & Sales",
  "Media & Entertainment",
  "Personal Services & Lifestyle",
  "Project Management",
  "Science & Research",
  "Sports & Fitness",
  "Technology & IT",
  "Writing & Content Creation"
];

const FindGig = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    province: '',
    area: '',
    traveling: '',
    skills: '',
    timeCommitment: '',
    pay: '',
    remote: ''
  });
  
  const [availableAreas, setAvailableAreas] = useState([]);

  useEffect(() => {
    loadListings();
  }, [filters]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const filterParams = {};
      
      // Build filter parameters
      if (filters.remote && filters.remote !== 'Any Structure') {
        filterParams.remote = filters.remote;
      }
      if (filters.skills && filters.skills !== 'All Skills') {
        filterParams.skills = [filters.skills];
      }
      if (filters.province) {
        filterParams.province = filters.province;
      }
      if (filters.area) {
        filterParams.area = filters.area;
      }

      const { listings: fetchedListings } = await listingService.getListings(
        { ...filterParams, type: 'gig' },
        20,
        0
      );
      
      // Filter out past gigs (gigs where the latest date has passed)
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Start of today
      
      const activeGigs = (fetchedListings || []).filter(listing => {
        // If no dates specified, keep the gig
        if (!listing.filters?.dates || listing.filters.dates.length === 0) {
          return true;
        }
        
        // Get the latest date from the gig's dates array
        const latestDate = new Date(Math.max(...listing.filters.dates.map(d => new Date(d))));
        latestDate.setHours(0, 0, 0, 0);
        
        // Keep gig if the latest date is today or in the future
        return latestDate >= now;
      });
      
      setListings(activeGigs);
    } catch (error) {
      console.error('Error loading listings:', error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'province') {
      // When province changes, reset area and update available areas
      setFilters(prev => ({ ...prev, province: value, area: '' }));
      setAvailableAreas(value ? locationData[value] : []);
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      province: '',
      area: '',
      traveling: '',
      skills: '',
      timeCommitment: '',
      pay: '',
      remote: ''
    });
    setAvailableAreas([]);
  };

  const handleGigClick = (listing) => {
    navigate(`/venture/${listing.id}`, { state: { listing } });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getTimeUntilGig = (dateString) => {
    const gigDate = new Date(dateString);
    const now = new Date();
    const diffTime = gigDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Past';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
  };

  return (
    <Container>
      <FiltersSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, color: '#FF6B6B', fontSize: '1.5rem' }}>Find Your Next Gig</h2>
          <ResetButton onClick={handleResetFilters}>Reset Filters</ResetButton>
        </div>
        
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Province</FilterLabel>
            <FilterSelect value={filters.province} onChange={(e) => handleFilterChange('province', e.target.value)}>
              <option value="">All Provinces</option>
              {provinces.map(province => (
                <option key={province} value={province}>{province}</option>
              ))}
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Area</FilterLabel>
            <FilterSelect 
              value={filters.area} 
              onChange={(e) => handleFilterChange('area', e.target.value)}
              disabled={!filters.province}
            >
              <option value="">All Areas</option>
              {availableAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Traveling Required</FilterLabel>
            <FilterSelect value={filters.traveling} onChange={(e) => handleFilterChange('traveling', e.target.value)}>
              <option value="">Any</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Skills Needed</FilterLabel>
            <FilterSelect value={filters.skills} onChange={(e) => handleFilterChange('skills', e.target.value)}>
              {skills.map(skill => (
                <option key={skill} value={skill}>{skill}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Time Commitment</FilterLabel>
            <FilterSelect value={filters.timeCommitment} onChange={(e) => handleFilterChange('timeCommitment', e.target.value)}>
              <option value="">Any Hours</option>
              <option value="1-4 hours">1-4 hours</option>
              <option value="4-8 hours">4-8 hours</option>
              <option value="8+ hours">8+ hours</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Work Structure</FilterLabel>
            <FilterSelect value={filters.remote} onChange={(e) => handleFilterChange('remote', e.target.value)}>
              <option value="">Any Structure</option>
              <option value="On-site">On-site</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Remote">Remote</option>
            </FilterSelect>
          </FilterGroup>
        </FiltersGrid>
      </FiltersSection>

      {loading ? (
        <LoadingContainer>
          <FaSpinner size={24} />
          <span style={{ marginLeft: '1rem' }}>Loading gigs...</span>
        </LoadingContainer>
      ) : (
        <>
          <GigsGrid>
            {listings.map(listing => {
              const { icon: IconComponent, gradient } = getIndustryIcon(listing.industry);
              return (
              <GigCard key={listing.id} onClick={() => handleGigClick(listing)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <GigIcon $gradient={gradient}>
                    <IconComponent />
                  </GigIcon>
                  {listing.filters?.dates?.[0] && <DateBadge>{getTimeUntilGig(listing.filters.dates[0])}</DateBadge>}
                </div>
                
                <GigTitle>{listing.title}</GigTitle>
                <GigDescription>{listing.short_description}</GigDescription>
                
                <GigDetails>
                  {listing.filters?.dates && listing.filters.dates.length > 0 && (
                    <DetailItem>
                      <FaCalendarAlt />
                      <span>
                        {listing.filters.dates.length === 1 
                          ? `Date: ${formatDate(listing.filters.dates[0])}`
                          : `Dates: ${listing.filters.dates.length} days available`
                        }
                      </span>
                    </DetailItem>
                  )}
                  <DetailItem>
                    <FaMapMarkerAlt />
                    <span>
                      Location: {(() => {
                        const loc = listing.location;
                        if (!loc?.province) return 'Not specified';
                        const displayArea = loc.area === 'Other' ? loc.customArea : loc.area;
                        return displayArea ? `${displayArea}, ${loc.province}` : loc.province;
                      })()}
                    </span>
                  </DetailItem>
                  <DetailItem>
                    <FaClock />
                    <span>Time commitment: {listing.time_commitment || 'Not specified'} hours</span>
                  </DetailItem>
                  <DetailItem>
                    <FaDollarSign />
                    <span>Fee: {capitalizeValue(listing.pay) || 'Not specified'}</span>
                  </DetailItem>
                  <DetailItem>
                    <span>
                      Skills: {listing.skills?.slice(0, 2).map(capitalizeValue).join(', ')}
                      {listing.skills?.length > 2 ? '...' : ''}
                    </span>
                  </DetailItem>
                  <DetailItem>
                    <span>Work structure: {capitalizeValue(listing.remote) || 'Not specified'}</span>
                  </DetailItem>
                </GigDetails>
              </GigCard>
            );
            })}
          </GigsGrid>

          {!loading && listings.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <h3>No gigs match your current filters</h3>
              <p>Try adjusting your filters to see more opportunities, or check back later for new listings</p>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default FindGig;